/**
 * 88. User Management — invite users; roles; status; MFA; session revoke;
 * invitation pause until roles assigned.
 *
 * Arabic and English (C7): every word from the catalogs; a member's status
 * agrees with them. Role names are shown as GET /v1/users sends them (names,
 * no codes), so a system role reads in the language it was seeded in;
 * registered in the C7-REST handoff.
 */
import { useId } from "react";
import {
  Card,
  PageHeader,
  Badge,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { PendingButton, PendingNote } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { formatDate } from "@/lib/money";

interface TenantUser {
  id: string;
  displayName: string;
  email: string;
  status: "Invited" | "Active" | "Suspended" | "Removed";
  mfaEnabled: boolean;
  roleNames: string[];
  lastLoginAt: string | null;
}

export function UserManagementPage() {
  const { t } = useTranslation();
  const pendingId = useId();
  const { data, isLoading } = useApiList<TenantUser>(
    ["admin", "users"],
    "/admin/users",
  );

  return (
    <div>
      <PageHeader
        title={t("admin.users.title")}
        subtitle={t("admin.users.subtitle")}
        actions={
          <PendingButton variant="primary" reasonId={pendingId}>
            {t("admin.users.invite")}
          </PendingButton>
        }
      />
      {/* The routes exist, but nothing yet stops the last administrator
          being suspended (SN-8), and an invitation cannot carry a role. The
          people screens come after the grant rules of the roles design
          (roles-and-responsibilities.md: RR-1, then RR-W1). */}
      <PendingNote id={pendingId} reason="people" slice="RR-W1" />
      {isLoading || !data ? (
        <Skeleton rows={8} />
      ) : (
        <Card title={listTitle(t("admin.users.list"), data.total)}>
          <DataTable<TenantUser>
            rows={data.items}
            columns={[
              { key: "displayName", label: t("fields.name") },
              {
                key: "email",
                label: t("fields.email"),
                render: (r) => <span dir="ltr">{r.email}</span>,
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <StatusBadge status={r.status} type="TenantUser" />
                ),
              },
              {
                key: "mfaEnabled",
                label: t("admin.users.mfa"),
                render: (r) =>
                  r.mfaEnabled ? (
                    <Badge tone="success">{t("admin.users.on")}</Badge>
                  ) : (
                    <Badge tone="default">{t("admin.users.off")}</Badge>
                  ),
              },
              {
                key: "roleNames",
                label: t("admin.users.roles"),
                render: (r) =>
                  (r.roleNames ?? []).join(t("shared.listSeparator")) || "—",
              },
              {
                key: "lastLoginAt",
                label: t("admin.users.lastLogin"),
                render: (r) => formatDate(r.lastLoginAt),
              },
              {
                key: "id",
                label: t("fields.actions"),
                render: (r) =>
                  r.status === "Invited" ? (
                    <PendingButton
                      size="sm"
                      variant="danger"
                      reasonId={pendingId}
                    >
                      {t("admin.users.cancelInvitation")}
                    </PendingButton>
                  ) : r.status === "Active" ? (
                    <PendingButton
                      size="sm"
                      variant="danger"
                      reasonId={pendingId}
                    >
                      {t("admin.users.suspend")}
                    </PendingButton>
                  ) : (
                    <PendingButton size="sm" reasonId={pendingId}>
                      {t("admin.users.activate")}
                    </PendingButton>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
